import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../../../shared/ListRowHover.css';
import './SupplierCostPricesPage.css';
import PageHeader from '../../../shared/PageHeader';
import SavedFilterTabs from '../../../shared/SavedFilterTabs';
import ReportLauncher from '../../../shared/ReportLauncher';
import CostPriceHistoryReportView from './CostPriceHistoryReportView';
import { PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize, Pagination } from '../../../shared/pagination';
import '../../../shared/StyledInput.css';
import DateInput from '../../../shared/DateInput';
import { formatDate } from '../../../shared/tenantClock';
import { formatMoney as formatMoneyShared, formatNumber, getTenantHomeCurrency } from '../../../shared/numberFormat';

const FILTER_SCREEN_KEY = 'supplier-cost-prices';
const DEFAULT_FILTERS = { status: 'all', search: '', category: '', costEffectiveFrom: '', costEffectiveTo: '' };
const FIELD_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };

// '—' for blank; `currency` marks the Supplier's foreign cost.
function formatMoney(value, currency) {
  return formatMoneyShared(value, { currency }) || '—';
}

function formatRate(value) {
  return formatNumber(value, { decimals: 6 }) || '—';
}

// Purchasing-side, Supplier-scoped Cost Price screen (issue #139) — browses
// this Supplier's own Products and their current Cost Price, with a
// drill-down to each Product's own dated schedule (ProductCostPricePage.js —
// the same page a non-Supplier-linked Product's kebab menu reaches). This
// page is deliberately just a browse/discovery list, not its own schedule
// editor — the schedule mechanics live in one place regardless of how a
// Product is reached. Tabs/search/pagination follow the same
// SavedFilterTabs + Pagination pattern as every other browse list (e.g.
// SupplierRebateSkuRateTable.js) rather than the unpaginated `all: true`
// fetch this page started out with.
function SupplierCostPricesPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  const [pagination, setPagination] = useState({ page: 1, limit, total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const [reportRows, setReportRows] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportFilterSummary, setReportFilterSummary] = useState('');

  useDefaultPageSize(setLimit);

  const updateFilter = (key, value) => { setFilters((prev) => ({ ...prev, [key]: value })); setPage(1); };
  const applyFilters = (values) => { setFilters(values); setPage(1); };

  // Human-readable summary of the current filters, stamped onto the report
  // subtitle (pat-architecture-reports-ui-standard.md) so a printed/exported
  // copy stays self-documenting once read later without the app open.
  const buildFilterSummary = () => {
    const parts = [`Status: ${{ all: 'All', active: 'Active', inactive: 'Inactive' }[filters.status] || 'All'}`];
    if (filters.search) parts.push(`Search "${filters.search}"`);
    if (filters.category) {
      const cat = categories.find((c) => String(c.id) === String(filters.category));
      parts.push(`Category: ${cat ? cat.name : filters.category}`);
    }
    if (filters.costEffectiveFrom || filters.costEffectiveTo) {
      parts.push(`Cost Effective ${formatDate(filters.costEffectiveFrom) || '…'} to ${formatDate(filters.costEffectiveTo) || '…'}`);
    }
    return parts.join(', ');
  };

  // Cost Price History Report — every Product matching the current filters,
  // ignoring the table's page size, same "fetch everything via all: true"
  // shape as Products' own Detail/Summary Report.
  const openReport = async () => {
    setReportOpen(true);
    setReportFilterSummary(buildFilterSummary());
    setReportLoading(true);
    try {
      const res = await axios.get('/api/products', {
        params: {
          supplier: id,
          status: filters.status,
          search: filters.search || undefined,
          category: filters.category || undefined,
          cost_effective_from: filters.costEffectiveFrom || undefined,
          cost_effective_to: filters.costEffectiveTo || undefined,
          sortBy: 'sku',
          sortOrder: 'ASC',
          all: true,
        },
      });
      setReportRows(res.data.data || []);
    } catch (err) {
      setReportRows([]);
    }
    setReportLoading(false);
  };

  useEffect(() => {
    axios.get(`/api/suppliers/${id}`)
      .then((res) => setSupplier(res.data.data))
      .catch((err) => setLoadError(err.response?.data?.message || 'Could not load this Supplier'));
  }, [id]);

  useEffect(() => {
    axios.get('/api/product-categories').then((res) => setCategories(res.data.data || [])).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    const fetchRows = async () => {
      setLoading(true);
      try {
        const res = await axios.get('/api/products', {
          params: {
            supplier: id,
            status: filters.status,
            search: filters.search || undefined,
            category: filters.category || undefined,
            cost_effective_from: filters.costEffectiveFrom || undefined,
            cost_effective_to: filters.costEffectiveTo || undefined,
            sortBy: 'sku',
            sortOrder: 'ASC',
            page,
            limit,
          },
        });
        setRows(res.data.data || []);
        setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
        setLoadError('');
      } catch (err) {
        setRows([]);
        setPagination({ page, limit, total: 0, pages: 1 });
        setLoadError(err.response?.data?.message || 'Could not load this Supplier\'s Products');
      }
      setLoading(false);
    };
    fetchRows();
  }, [id, filters, page, limit]);

  if (loadError) return <div style={{ padding: 24, color: 'red' }}>{loadError}</div>;
  if (!supplier) return loading ? <div style={{ padding: 24 }}>Loading...</div> : null;

  // Only a Supplier trading in a foreign currency carries a rate (issue #303).
  const showRate = !!supplier.currency_code && supplier.currency_code !== getTenantHomeCurrency();
  const columnCount = showRate ? 7 : 5;

  return (
    <div className="supplier-cost-prices-page" style={{ width: '100%', maxWidth: 1080, margin: '32px auto 48px', background: '#f6f8fa', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: '20px 24px 32px' }}>
      <PageHeader
        crumbs={[
          { label: 'Suppliers', onClick: () => navigate('/suppliers') },
          { label: 'Cost Price Schedule' },
        ]}
        title="Cost Price Schedule"
        subtitle={<strong>{supplier.name} — {supplier.currency_code}</strong>}
      />

      <div style={{ display: 'flex', gap: 10, margin: '16px 0 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <input
            placeholder="Search SKU or name..."
            value={filters.search}
            onChange={(e) => updateFilter('search', e.target.value)}
            style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: 240 }}
          />
          {filters.search && (
            <button type="button" onClick={() => updateFilter('search', '')} title="Clear search" style={{ marginLeft: 6, padding: '4px 8px', fontSize: 12 }}>×</button>
          )}
        </div>
        <select className="styled-input" value={filters.category} onChange={(e) => updateFilter('category', e.target.value)} style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14 }}>
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{'   '.repeat(c.depth || 0)}{c.name}</option>
          ))}
        </select>
        <ReportLauncher
          options={[{ key: 'cost-price-history', label: 'Cost Price History Report', onSelect: openReport }]}
          activeContent={reportOpen ? (
            <CostPriceHistoryReportView
              products={reportRows}
              loading={reportLoading}
              filterSummary={reportFilterSummary}
              currencyCode={supplier.currency_code}
              onClose={() => setReportOpen(false)}
            />
          ) : null}
          onCloseActive={() => setReportOpen(false)}
        />
      </div>

      <div style={{ margin: '10px 0 0' }}>
        <SavedFilterTabs
          screenKey={FILTER_SCREEN_KEY}
          defaultFilters={DEFAULT_FILTERS}
          currentFilters={filters}
          onApply={applyFilters}
          renderFields={(draft, setDraftField) => (
            <>
              <div>
                <label style={FIELD_LABEL_STYLE}>Search</label><br />
                <input
                  placeholder="Search SKU or name..."
                  value={draft.search}
                  onChange={(e) => setDraftField('search', e.target.value)}
                  style={FIELD_INPUT_STYLE}
                />
              </div>
              <div>
                <label style={FIELD_LABEL_STYLE}>Status</label><br />
                <select className="styled-input" value={draft.status} onChange={(e) => setDraftField('status', e.target.value)} style={FIELD_INPUT_STYLE}>
                  <option value="all">All Products</option>
                  <option value="active">Active Products</option>
                  <option value="inactive">Inactive Products</option>
                </select>
              </div>
              <div>
                <label style={FIELD_LABEL_STYLE}>Category</label><br />
                <select className="styled-input" value={draft.category} onChange={(e) => setDraftField('category', e.target.value)} style={FIELD_INPUT_STYLE}>
                  <option value="">All categories</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{'   '.repeat(c.depth || 0)}{c.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <div>
                  <label style={FIELD_LABEL_STYLE}>Cost Effective From</label><br />
                  <DateInput
                    value={draft.costEffectiveFrom}
                    onChange={(e) => setDraftField('costEffectiveFrom', e.target.value)}
                    style={FIELD_INPUT_STYLE}
                  />
                </div>
                <div>
                  <label style={FIELD_LABEL_STYLE}>Cost Effective To</label><br />
                  <DateInput
                    value={draft.costEffectiveTo}
                    onChange={(e) => setDraftField('costEffectiveTo', e.target.value)}
                    style={FIELD_INPUT_STYLE}
                  />
                </div>
              </div>
            </>
          )}
        />
      </div>

      <div style={{ background: '#fff', borderRadius: '0 8px 8px 8px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '10%' }} />
              <col style={{ width: showRate ? '23%' : '38%' }} />
              <col style={{ width: '12%' }} />
              {showRate && <col style={{ width: '13%' }} />}
              {showRate && <col style={{ width: '12%' }} />}
              <col style={{ width: '13%' }} />
              <col style={{ width: '17%' }} />
            </colgroup>
            <thead className="list-head">
              <tr>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>SKU</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Name</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Cost ({supplier.currency_code})</th>
                {showRate && <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Exchange Rate</th>}
                {showRate && <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>{getTenantHomeCurrency()}</th>}
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Effective From</th>
                <th style={{ padding: '10px 12px' }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={columnCount} style={{ padding: '14px', textAlign: 'center', color: '#888' }}>Loading...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={columnCount} style={{ padding: '14px', textAlign: 'center', color: '#888' }}>No Products found.</td></tr>
              ) : rows.map((item) => {
                // The current entry's own rate — "—" while its rate is missing.
                const exchangeRate = item.cost_price_exchange_rate ?? null;
                const audCost = item.cost_price_foreign !== null && exchangeRate !== null && exchangeRate !== undefined
                  ? Number(item.cost_price_foreign) * Number(exchangeRate)
                  : null;
                return (
                  <tr key={item.id} className="list-row">
                    <td style={{ padding: '6px 12px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.sku}>{item.sku}</td>
                    <td style={{ padding: '6px 12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.name}>{item.name}</td>
                    <td style={{ padding: '6px 12px', textAlign: 'right' }} title={item.cost_price_foreign === null ? 'No Cost Price entry covers today' : undefined}>
                      {item.cost_price_foreign === null ? '—' : formatMoney(item.cost_price_foreign, supplier.currency_code)}
                    </td>
                    {showRate && <td style={{ padding: '6px 12px', textAlign: 'right', color: '#888' }} title={item.cost_price_foreign !== null && exchangeRate === null ? 'Rate missing' : undefined}>{formatRate(exchangeRate)}</td>}
                    {showRate && <td style={{ padding: '6px 12px', textAlign: 'right', color: '#888' }}>{formatMoney(audCost)}</td>}
                    <td style={{ padding: '6px 12px' }}>{formatDate(item.cost_price_effective_from) || '—'}</td>
                    <td style={{ padding: '6px 12px', textAlign: 'right' }}>
                      <button type="button" className="action-button" onClick={() => navigate(`/products/${item.id}/cost-prices`, { state: { fromSupplier: { id: supplier.id, name: supplier.name } } })}>Cost Price</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        pagination={pagination}
        setPage={setPage}
        rightContent={(
          <select className="styled-input" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14 }}>
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default SupplierCostPricesPage;
